"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectorMapMethod = void 0;
/**
 * To use this hook, the component using it must have access to a Provider whose redux store
 * complies with the ValidReduxState type.
 */
var react_redux_1 = require("react-redux");
var react_1 = require("react");
var lodash_1 = require("lodash");
var slice_1 = require("../../slices/carPolicies/slice");
var carPolicies_1 = require("../../functions/carPolicies");
var actions_1 = require("../../slices/carPolicies/actions");
/** Used external hooks */
var useResettableNetworkStatus_1 = __importDefault(require("../useResettableNetworkStatus"));
/** Local functions */
var getMostRecentPolicySource_1 = __importDefault(require("./getMostRecentPolicySource"));
/** How long to wait before firing the next check request */
var DEBOUNCE_TIMEOUT_MS = 1000;
/**
 * Allows us to extract the required data from the redux store.
 * This is why, in order to use this hook in your project, the rendering component must be located
 * inside a redux provider which serves a state that conforms to `ValidReduxState`. See the
 * slices/carPolicies section.
 *
 * @param state - Reference to a valid redux state
 */
exports.selectorMapMethod = function (state) { return state.carPolicies; };
function useAlreadyInsuredCarCheck(_a) {
    var licenseValidationError = _a.licenseValidationError, vinNumberValidationError = _a.vinNumberValidationError, motorNumberValidationError = _a.motorNumberValidationError, onMostRecentPolicyFound = _a.onMostRecentPolicyFound, client = _a.client, licensePlate = _a.licensePlate, vinNumber = _a.vinNumber, motorNumber = _a.motorNumber, onVinAndSerialMustReset = _a.onVinAndSerialMustReset, onSerialMotorMustReset = _a.onSerialMotorMustReset, _b = _a.isNewCar, isNewCar = _b === void 0 ? true : _b, _c = _a.disableHasActivePolicyValidation, disableHasActivePolicyValidation = _c === void 0 ? false : _c;
    /** True if one of the request is running */
    var _d = react_1.useState(false), updatingData = _d[0], toggleUpdatingData = _d[1];
    /**
     * To prevent logic from executing as soon as the screen renders, we keep track on whether this
     * call corresponds to the first render, and block certain actions accordingly
     */
    var _e = react_1.useState(true), isFirstRender = _e[0], toggleIsFirstRender = _e[1];
    react_1.useEffect(function () {
        toggleIsFirstRender(false);
    }, []);
    /**
     * To perform redux transactions without defining actions, we need a reference to
     * the dispatch object directly.
     */
    var dispatch = react_redux_1.useDispatch();
    /**
     * Extract the policy list from the store.
     */
    var _f = react_redux_1.useSelector(exports.selectorMapMethod), carPolicyForLicense = _f.carPolicyForLicense, carPolicyForVin = _f.carPolicyForVin, carPolicyForMotor = _f.carPolicyForMotor, mostRecentCarPolicy = _f.mostRecentCarPolicy, policiesForLicenseRequestStatus = _f.policiesForLicenseRequestStatus, policiesForVinRequestStatus = _f.policiesForVinRequestStatus, policiesForMotorRequestStatus = _f.policiesForMotorRequestStatus, reportFoundPolicyData = _f.reportFoundPolicyData;
    /**
     * This is the variable that will be used to check if there is an active policy for either
     * the license plate, the vin number, or motor number
     */
    var hasActivePolicy = react_1.useMemo(function () {
        return carPolicies_1.isActive(carPolicyForLicense) ||
            carPolicies_1.isActive(carPolicyForVin) ||
            carPolicies_1.isActive(carPolicyForMotor);
    }, [carPolicyForLicense, carPolicyForVin, carPolicyForMotor]);
    /**
     * When the license check request returns an empty response, and there was already a "valid" and active
     * policy for the vinNumber or motorNumber, we need to erase the original field's values. Since this hook doesn't
     * directly controls the fields (rather, it connects to the field's values), we need to expose the
     * `onVinAndSerialMustReset` callback and call it accordingly.
     *
     * This callback must reset the values to empty strings.
     */
    var localPoliciesForLicenseRequestStatus = useResettableNetworkStatus_1.default(policiesForLicenseRequestStatus);
    react_1.useEffect(function () {
        if (!isFirstRender &&
            localPoliciesForLicenseRequestStatus === 'success' &&
            onVinAndSerialMustReset) {
            onVinAndSerialMustReset();
            dispatch(actions_1.resetVinPolicy());
            dispatch(actions_1.resetMotorPolicy());
        }
    }, [localPoliciesForLicenseRequestStatus, licensePlate]);
    var localPoliciesForVinRequestStatus = useResettableNetworkStatus_1.default(policiesForVinRequestStatus);
    react_1.useEffect(function () {
        if (!isFirstRender &&
            localPoliciesForVinRequestStatus === 'success' &&
            ((!isNewCar && disableHasActivePolicyValidation) || hasActivePolicy) &&
            onSerialMotorMustReset) {
            onSerialMotorMustReset();
            dispatch(actions_1.resetMotorPolicy());
        }
    }, [localPoliciesForVinRequestStatus, hasActivePolicy]);
    /**
     * True if the vin needs to still be validated, but the trigger has not been fired yet.
     */
    var vinValidationIsPending = react_1.useMemo(function () { return !carPolicyForLicense && policiesForVinRequestStatus !== 'success'; }, [carPolicyForLicense, policiesForVinRequestStatus]);
    /**
     * True if the motor needs to still be validated, but the trigger has not been fired yet.
     */
    var motorValidationIsPending = react_1.useMemo(function () {
        return !carPolicyForLicense &&
            !carPolicyForVin &&
            policiesForMotorRequestStatus !== 'success';
    }, [carPolicyForLicense, carPolicyForVin, policiesForMotorRequestStatus]);
    var shouldDisableVin = react_1.useMemo(function () { return carPolicies_1.isActive(carPolicyForLicense); }, [
        carPolicyForLicense,
    ]);
    var shouldDisableMotor = react_1.useMemo(function () { return carPolicies_1.isActive(carPolicyForLicense) || carPolicies_1.isActive(carPolicyForVin); }, [carPolicyForLicense, carPolicyForVin]);
    /**
     * The logic that will provide for policies for the provided license plate. Note that
     * the check will not be fired on the first render, which will prevent "backs" from
     * overwriting previously defined data.
     */
    var debouncedLicenseCheck = react_1.useRef(lodash_1.debounce(function (dLicensePlate, dError) {
        if (!dError && dLicensePlate !== '') {
            dispatch(actions_1.requestExistingPoliciesForLicense(dLicensePlate, client, isNewCar, disableHasActivePolicyValidation));
        }
    }, DEBOUNCE_TIMEOUT_MS)).current;
    /**
     * Fires the vin checking request
     */
    var debouncedVinCheck = react_1.useRef(lodash_1.debounce(function (dVinNumber, dError) {
        if (!dError && dVinNumber !== '') {
            dispatch(actions_1.requestExistingPoliciesForVin(dVinNumber, client, isNewCar, disableHasActivePolicyValidation));
        }
    }, DEBOUNCE_TIMEOUT_MS)).current;
    /**
     * Fires the motor checking request
     */
    var debouncedMotorCheck = react_1.useRef(lodash_1.debounce(function (dMotorNumber, dError) {
        if (!dError && dMotorNumber !== '') {
            dispatch(actions_1.requestExistingPoliciesForMotor(dMotorNumber, client));
        }
    }, DEBOUNCE_TIMEOUT_MS)).current;
    /**
     * Triggers the debounce check for the license, or resets the validation logic if the provided license
     * is empty.
     */
    react_1.useEffect(function () {
        /** If it detects an error, cancel any pending calls to the debounced request */
        if (licenseValidationError) {
            debouncedLicenseCheck.cancel();
            return;
        }
        if (!isFirstRender) {
            toggleUpdatingData(true);
            debouncedLicenseCheck(licensePlate, licenseValidationError);
        }
    }, [debouncedLicenseCheck, licensePlate, licenseValidationError]);
    /**
     * Hook that fires the next vin check if conditions are met, or resets the vin validation logic if the
     * data is empty.
     */
    react_1.useEffect(function () {
        /** If it detects an error, cancel any pending calls to the debounced request */
        if (vinNumberValidationError) {
            debouncedVinCheck.cancel();
            return;
        }
        /** Only check for vin if these conditions are met */
        if (!isFirstRender && !carPolicies_1.isActive(carPolicyForLicense)) {
            toggleUpdatingData(true);
            debouncedVinCheck(vinNumber, vinNumberValidationError);
        }
        if (vinNumber === '') {
            dispatch(actions_1.resetVinPolicy());
        }
    }, [vinNumber, vinNumberValidationError]);
    /**
     * Hook that fires the next motor check if conditions are met, or resets the motor validation logic if the
     * data is empty.
     */
    react_1.useEffect(function () {
        /** If it detects an error, cancel any pending calls to the debounced request */
        if (motorNumberValidationError) {
            debouncedMotorCheck.cancel();
            return;
        }
        /** Only check for vin if these conditions are met */
        if (!isFirstRender &&
            !carPolicies_1.isActive(carPolicyForVin) &&
            !carPolicies_1.isActive(carPolicyForLicense)) {
            toggleUpdatingData(true);
            debouncedMotorCheck(motorNumber, motorNumberValidationError);
        }
        if (motorNumber === '') {
            dispatch(actions_1.resetMotorPolicy());
        }
    }, [motorNumber, motorNumberValidationError]);
    /**
     * Sets updating data to "false" when the request finishes
     */
    react_1.useEffect(function () {
        if (policiesForLicenseRequestStatus === 'success') {
            toggleUpdatingData(false);
        }
    }, [policiesForLicenseRequestStatus]);
    react_1.useEffect(function () {
        if (policiesForVinRequestStatus === 'success') {
            toggleUpdatingData(false);
        }
    }, [policiesForVinRequestStatus]);
    react_1.useEffect(function () {
        if (policiesForMotorRequestStatus === 'success') {
            toggleUpdatingData(false);
        }
    }, [policiesForMotorRequestStatus]);
    /**
     * To detect if the last active policy come from the license request, vin or the motor request, we keep track
     * here of the original soruce of the `mostRecentCarPolicy` variable via an union type, which can be
     * either 'licensePlate', 'vinNumber', 'motorNUmber', or '', depending on this useMemo's internal logic.
     */
    var mostRecentPolicySource = react_1.useMemo(function () {
        return getMostRecentPolicySource_1.default({
            carPolicyForLicense: carPolicyForLicense,
            carPolicyForVin: carPolicyForVin,
            carPolicyForMotor: carPolicyForMotor,
            mostRecentCarPolicy: mostRecentCarPolicy,
        });
    }, [
        carPolicyForLicense,
        carPolicyForVin,
        carPolicyForMotor,
        mostRecentCarPolicy,
    ]);
    /**
     * Notify when a "most recent" car policy is found, which can come from either the license plate, vin, or motor
     * check requests.
     */
    react_1.useEffect(function () {
        if (!isFirstRender &&
            typeof mostRecentCarPolicy !== 'undefined' &&
            // don't check it has an active policy if "disableHasActivePolicyValidation" is true
            (disableHasActivePolicyValidation || !hasActivePolicy) &&
            ![
                policiesForLicenseRequestStatus,
                policiesForVinRequestStatus,
                policiesForMotorRequestStatus,
            ].includes('requesting') &&
            reportFoundPolicyData &&
            /**
             * Validate if mostRecentPolicySource is diferent from motorNumber just when has active policy validaion is enable ( true )
             * if has active policy validation is disable ( false ) call onMostRecentPolicyFound
             */
            (!disableHasActivePolicyValidation ||
                mostRecentPolicySource !== 'motorNumber')) {
            onMostRecentPolicyFound(mostRecentCarPolicy);
            return function () {
                dispatch(slice_1.updateReportPolicyData(false));
            };
        }
        return function () {
            // empty function
        };
    }, [
        mostRecentCarPolicy,
        hasActivePolicy,
        policiesForLicenseRequestStatus,
        policiesForVinRequestStatus,
        policiesForMotorRequestStatus,
        reportFoundPolicyData,
    ]);
    return {
        hasActivePolicy: hasActivePolicy,
        mostRecentCarPolicy: mostRecentCarPolicy,
        licensePlateRequestStatus: policiesForLicenseRequestStatus,
        vinRequestStatus: policiesForVinRequestStatus,
        motorRequestStatus: policiesForMotorRequestStatus,
        vinValidationIsPending: vinValidationIsPending,
        motorValidationIsPending: motorValidationIsPending,
        shouldDisableVin: shouldDisableVin,
        shouldDisableMotor: shouldDisableMotor,
        updatingData: updatingData,
        mostRecentPolicySource: mostRecentPolicySource,
    };
}
exports.default = useAlreadyInsuredCarCheck;
//# sourceMappingURL=useAlreadyInsuredCarCheck.js.map